import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../ui/button';
import {
  Lock,
  Key,
  Check,
  X,
  Lightbulb,
  Loader2,
  RotateCcw,
} from 'lucide-react';

// Noir-themed safe dial cipher interface
// Evokes 1940s bank heist movies with a rotating safe combination dial
export const SafeDialUI = ({
  ciphertext,
  hints,
  onSubmit,
  loading,
  result,
  attempts,
  solved,
  cipherType,
}) => {
  const [solution, setSolution] = useState('');
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const dialRef = useRef(null);

  // Dial letters for visual effect
  const dialLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Handle input change with dial spin animation
  const handleInputChange = (value) => {
    const newValue = value.toUpperCase().replace(/[^A-Z ]/g, '');
    setSolution(newValue);

    // Spin dial on each character
    if (newValue.length > solution.length) {
      const newChar = newValue.charAt(newValue.length - 1);
      const charIndex = dialLetters.indexOf(newChar);
      if (charIndex >= 0) {
        setIsSpinning(true);
        setRotation(-(charIndex * (360 / 26)));
        setTimeout(() => setIsSpinning(false), 300);
      }
    }
  };

  // Handle letter click on dial
  const handleDialClick = (letter) => {
    handleInputChange(solution + letter);
  };

  // Clear solution
  const handleClear = () => {
    setSolution('');
    setRotation(0);
  };

  // Handle submit
  const handleSubmit = () => {
    if (solution.trim()) {
      onSubmit(solution.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && solution.trim()) {
      handleSubmit();
    }
  };

  return (
    <div className="space-y-6">
      {/* Safe Dial Visual */}
      <div className="flex flex-col items-center">
        <div className="relative w-64 h-64 mx-auto mb-4">
          {/* Outer ring - metallic safe border */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-zinc-600 via-zinc-800 to-zinc-900 shadow-2xl border-4 border-zinc-500">
            {/* Inner dial shadow */}
            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 shadow-inner" />
          </div>

          {/* Rotating dial */}
          <div
            ref={dialRef}
            className={`absolute inset-6 rounded-full bg-gradient-to-br from-zinc-800 to-black border-2 border-zinc-600 transition-transform ${isSpinning ? 'duration-300' : 'duration-100'}`}
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            {/* Dial markings */}
            {dialLetters.map((letter, i) => {
              const angle = (i * 360) / 26;
              const radians = (angle - 90) * (Math.PI / 180);
              const radius = 85;
              const x = 50 + Math.cos(radians) * (radius / 2 + 15);
              const y = 50 + Math.sin(radians) * (radius / 2 + 15);

              return (
                <button
                  key={letter}
                  onClick={() => handleDialClick(letter)}
                  disabled={loading || solved}
                  className="absolute text-xs font-bold text-red-500 hover:text-red-300 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                  }}
                >
                  {letter}
                </button>
              );
            })}

            {/* Center dial knob */}
            <div className="absolute inset-16 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-900 border-2 border-zinc-500 flex items-center justify-center shadow-lg">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
          </div>

          {/* Dial pointer */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
            <div className="w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-red-600" />
          </div>
        </div>

        {/* Dial instructions */}
        <p className="text-xs text-slate-500 italic text-center">
          Click letters on the dial or type your solution below
        </p>
      </div>

      {/* Ciphertext Display - styled as a note */}
      <div className="bg-amber-900/20 border-2 border-amber-800/50 rounded-lg p-4 relative">
        <div className="absolute -top-3 left-4 bg-slate-900 px-2">
          <span className="text-xs text-amber-600 uppercase tracking-wider font-serif">Encoded Message</span>
        </div>
        <p className="font-serif text-lg text-amber-200 tracking-widest leading-relaxed break-all pt-2">
          {ciphertext}
        </p>
      </div>

      {/* Discovered Hints - styled as case notes */}
      {hints && hints.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-serif text-amber-400">Case Notes</span>
          </div>
          <div className="bg-slate-800/50 rounded border border-slate-700 p-3 space-y-2">
            {hints.map((hint) => (
              <p key={hint.id} className="text-sm text-slate-300 font-serif italic border-l-2 border-red-600 pl-3">
                "{hint.hint_phrase}"
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div
          className={`p-4 rounded-lg border-2 transition-all duration-300 ${
            result.correct
              ? 'bg-green-900/30 border-green-600 animate-success-glow'
              : 'bg-red-900/30 border-red-600 animate-shake'
          }`}
          style={result.correct ? { boxShadow: '0 0 30px rgba(34, 197, 94, 0.4)' } : {}}
        >
          <div className="flex items-center gap-2 mb-2">
            {result.correct ? (
              <Check className="w-6 h-6 text-green-500 animate-bounce" />
            ) : (
              <X className="w-5 h-5 text-red-500" />
            )}
            <span className={`font-serif font-bold text-lg ${result.correct ? 'text-green-400' : 'text-red-400'}`}>
              {result.correct ? 'Safe Cracked!' : 'Wrong Combination'}
            </span>
          </div>
          <p className={`text-sm font-serif ${result.correct ? 'text-green-300' : 'text-red-300'}`}>
            {result.message}
          </p>
          {result.correct && result.plaintext && (
            <div className="mt-3 p-3 bg-green-800/30 rounded border border-green-600">
              <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Decoded Message:</p>
              <p className="text-green-200 font-serif font-bold text-lg">
                {result.plaintext}
              </p>
            </div>
          )}
          {!result.correct && result.escalation_hint && (
            <p className="mt-2 text-amber-300 text-sm font-serif italic">
              {result.escalation_hint}
            </p>
          )}
        </div>
      )}

      {/* Solution Input - styled as an old typewriter input */}
      <div className="space-y-3">
        <div className="relative">
          <input
            type="text"
            value={solution}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="ENTER DECODED MESSAGE..."
            disabled={loading || solved}
            className="w-full bg-slate-900 border-2 border-red-900/50 rounded-lg px-4 py-3 text-white font-serif tracking-widest placeholder:text-slate-600 focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/30 disabled:opacity-50"
          />
          {solution && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              disabled={loading || solved}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 font-serif">
          Attempts: {attempts || 0}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleSubmit}
          disabled={loading || !solution.trim() || solved}
          className="flex-1 bg-red-700 hover:bg-red-600 text-white font-serif border-2 border-red-900"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <Key className="w-4 h-4 mr-2" />
              Crack the Safe
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default SafeDialUI;
